import { create } from 'zustand';

export type MgOStage =
  | 'setup'
  | 'crucible-weighed'
  | 'mg-polished'
  | 'mg-placed'
  | 'mg-weighed'
  | 'apparatus-ready'
  | 'heating-open'
  | 'igniting'
  | 'lid-on'
  | 'final-heating'
  | 'cooling'
  | 'final-weighed'
  | 'constant-mass'
  | 'observed'
  | 'completed';

export type TrialId = 1 | 2 | 3;
export type CameraView = 'default' | 'top' | 'side' | 'closeup' | 'workbench';
export type WeighType = 'empty' | 'with-mg' | 'final';

export interface TrialPreset {
  id: TrialId;
  ribbonLengthCm: number;
  mgMassG: number;
}

export const TRIAL_PRESETS: Record<TrialId, TrialPreset> = {
  1: { id: 1, ribbonLengthCm: 5,  mgMassG: 0.12 },
  2: { id: 2, ribbonLengthCm: 10, mgMassG: 0.24 },
  3: { id: 3, ribbonLengthCm: 15, mgMassG: 0.36 },
};

export interface TrialResult {
  trialId: TrialId;
  ribbonLengthCm: number;
  crucibleLidMassG: number;
  crucibleLidMgMassG: number;
  crucibleLidMgOMassG: number;
  mgMassG: number;
  oxygenMassG: number;
  molesMg: number;
  molesO: number;
  ratioMgToO: number;
  empiricalFormula: string;
  recordedAt: number;
}

interface MgOState {
  currentTrial: TrialId;
  labStage: MgOStage;

  crucibleLidMassG: number;
  mgInCrucibleG: number;
  mgOInCrucibleG: number;
  currentReadingG: number;
  targetReadingG: number;

  isHeating: boolean;
  heatIntensity: number;
  crucibleTempC: number;

  lidIsOn: boolean;
  lidLiftCount: number;
  lidLiftSecondsTotal: number;
  fumeLossG: number;

  reheatCycle: number;
  lastWeighingG: number;

  // Animation flags
  isPolishingMg: boolean;
  polishProgress: number;
  isPlacingMg: boolean;
  placeMgProgress: number;
  isPlacingOnTripod: boolean;
  placeOnTripodProgress: number;
  isPlacingLid: boolean;
  lidPlaceProgress: number;
  isLiftingLid: boolean;
  lidLiftProgress: number;
  isIgniting: boolean;
  ignitionProgress: number;
  isCooling: boolean;
  isFinalHeating: boolean;
  finalHeatProgress: number;
  isReheating: boolean;
  reheatProgress: number;
  isWeighing: boolean;
  weighProgress: number;
  weighType: WeighType | null;

  experimentTime: number;
  results: TrialResult[];

  simulationSpeed: number;
  cameraView: CameraView;
  cameraVersion: number;
  showTutorial: boolean;

  // Actions
  selectTrial: (id: TrialId) => void;
  weighCrucibleAndLid: () => void;
  polishMagnesium: () => void;
  placeMagnesiumInCrucible: () => void;
  weighCrucibleWithMg: () => void;
  placeOnTripod: () => void;
  toggleHeating: () => void;
  setHeatIntensity: (i: number) => void;
  placeLid: () => void;
  liftLid: () => void;
  startFinalHeating: () => void;
  startCooling: () => void;
  weighFinal: () => void;
  checkConstantMass: () => void;
  recordObservation: () => void;
  resetTrial: () => void;
  resetAll: () => void;
  setSimulationSpeed: (s: number) => void;
  cycleSimulationSpeed: () => void;
  setCameraView: (v: CameraView) => void;
  setShowTutorial: (show: boolean) => void;
  tick: (delta: number) => void;
}

const SPEED_CYCLE = [1, 2, 5, 10];
const CRUCIBLE_LID_MASS_G = 25.00;
const POLISH_DURATION = 2.0;
const PLACE_MG_DURATION = 1.6;
const PLACE_TRIPOD_DURATION = 1.8;
const PLACE_LID_DURATION = 1.2;
const LIFT_LID_DURATION = 2.8;
const IGNITION_DURATION = 3.0;
const FINAL_HEAT_DURATION = 8.0;
const REHEAT_DURATION = 5.0;
const WEIGH_DURATION = 3.0;
const FUME_LOSS_PER_LIFT_G = 0.005;
const FUME_LOSS_CAP_FRACTION = 0.35;
const IGNITION_TEMP_C = 450;
const STRONG_HEAT_TARGET_C = 880;

function buildInitialTrialState(id: TrialId) {
  return {
    currentTrial: id,
    labStage: 'setup' as MgOStage,
    crucibleLidMassG: CRUCIBLE_LID_MASS_G,
    mgInCrucibleG: 0,
    mgOInCrucibleG: 0,
    currentReadingG: 0,
    targetReadingG: 0,
    isHeating: false,
    heatIntensity: 0.8,
    crucibleTempC: 25,
    lidIsOn: false,
    lidLiftCount: 0,
    lidLiftSecondsTotal: 0,
    fumeLossG: 0,
    reheatCycle: 0,
    lastWeighingG: 0,
    isPolishingMg: false,
    polishProgress: 0,
    isPlacingMg: false,
    placeMgProgress: 0,
    isPlacingOnTripod: false,
    placeOnTripodProgress: 0,
    isPlacingLid: false,
    lidPlaceProgress: 0,
    isLiftingLid: false,
    lidLiftProgress: 0,
    isIgniting: false,
    ignitionProgress: 0,
    isCooling: false,
    isFinalHeating: false,
    finalHeatProgress: 0,
    isReheating: false,
    reheatProgress: 0,
    isWeighing: false,
    weighProgress: 0,
    weighType: null as WeighType | null,
    experimentTime: 0,
  };
}

export const useMagnesiumOxideStore = create<MgOState>((set, get) => ({
  ...buildInitialTrialState(1),
  results: [],
  simulationSpeed: 1,
  cameraView: 'default',
  cameraVersion: 0,
  showTutorial: false,

  selectTrial: (id) => {
    set({ ...buildInitialTrialState(id) });
  },

  weighCrucibleAndLid: () => set((s) => {
    if (s.labStage !== 'setup' || s.isWeighing) return {};
    // Pan starts empty as the crucible lifts off the bench
    return {
      isWeighing: true,
      weighProgress: 0,
      weighType: 'empty',
      targetReadingG: 0,
    };
  }),

  polishMagnesium: () => set((s) => {
    if (s.labStage !== 'crucible-weighed' || s.isPolishingMg) return {};
    return { isPolishingMg: true, polishProgress: 0 };
  }),

  placeMagnesiumInCrucible: () => set((s) => {
    if (s.labStage !== 'mg-polished' || s.isPlacingMg) return {};
    return { isPlacingMg: true, placeMgProgress: 0 };
  }),

  weighCrucibleWithMg: () => set((s) => {
    if (s.labStage !== 'mg-placed' || s.isWeighing) return {};
    return {
      isWeighing: true,
      weighProgress: 0,
      weighType: 'with-mg',
      targetReadingG: 0,
    };
  }),

  placeOnTripod: () => set((s) => {
    if (s.labStage !== 'mg-weighed' || s.isPlacingOnTripod) return {};
    return { isPlacingOnTripod: true, placeOnTripodProgress: 0 };
  }),

  toggleHeating: () => set((s) => {
    if (s.labStage === 'setup' || s.labStage === 'crucible-weighed' || s.labStage === 'mg-polished'
      || s.labStage === 'mg-placed' || s.labStage === 'mg-weighed') return {};
    return { isHeating: !s.isHeating };
  }),

  setHeatIntensity: (i) => set({ heatIntensity: Math.max(0, Math.min(1, i)) }),

  placeLid: () => set((s) => {
    if (s.lidIsOn || s.isPlacingLid) return {};
    if (s.labStage !== 'igniting' && s.labStage !== 'heating-open') return {};
    return { isPlacingLid: true, lidPlaceProgress: 0 };
  }),

  liftLid: () => set((s) => {
    if (!s.lidIsOn || s.isLiftingLid) return {};
    if (s.labStage !== 'lid-on') return {};
    return { isLiftingLid: true, lidLiftProgress: 0 };
  }),

  startFinalHeating: () => set((s) => {
    if (s.labStage !== 'lid-on') return {};
    return {
      lidIsOn: false,
      labStage: 'final-heating',
      isFinalHeating: true,
      finalHeatProgress: 0,
      isHeating: true,
    };
  }),

  startCooling: () => set((s) => {
    if (s.labStage !== 'final-heating') return {};
    return {
      labStage: 'cooling',
      isCooling: true,
      isHeating: false,
      isFinalHeating: false,
    };
  }),

  weighFinal: () => set((s) => {
    if (s.labStage !== 'cooling' || s.crucibleTempC > 60 || s.isWeighing) return {};
    return {
      isWeighing: true,
      weighProgress: 0,
      weighType: 'final',
      targetReadingG: 0,
    };
  }),

  checkConstantMass: () => set((s) => {
    if (s.labStage !== 'final-weighed' || s.isReheating) return {};
    if (s.reheatCycle >= 1) {
      return { labStage: 'constant-mass' };
    }
    return {
      isReheating: true,
      reheatProgress: 0,
    };
  }),

  recordObservation: () => set((s) => {
    if (s.labStage !== 'constant-mass') return {};
    const preset = TRIAL_PRESETS[s.currentTrial];
    const mgMassG = preset.mgMassG;
    const theoreticalMgO = mgMassG * (40 / 24);
    const fumeCap = theoreticalMgO * FUME_LOSS_CAP_FRACTION;
    const cappedFumes = Math.min(s.fumeLossG, fumeCap);
    const finalContentG = theoreticalMgO - cappedFumes;
    const finalTotalG = CRUCIBLE_LID_MASS_G + finalContentG;
    const oxygenMassG = finalContentG - mgMassG;
    const molesMg = mgMassG / 24;
    const molesO = Math.max(0, oxygenMassG) / 16;
    const ratio = molesMg > 0 ? molesO / molesMg : 0;
    const ratioRounded = Math.round(ratio * 100) / 100;
    const empiricalFormula =
      ratioRounded >= 0.97 && ratioRounded <= 1.03
        ? 'MgO'
        : `MgO${ratioRounded.toFixed(2)}`;
    const result: TrialResult = {
      trialId: s.currentTrial,
      ribbonLengthCm: preset.ribbonLengthCm,
      crucibleLidMassG: parseFloat(CRUCIBLE_LID_MASS_G.toFixed(2)),
      crucibleLidMgMassG: parseFloat((CRUCIBLE_LID_MASS_G + mgMassG).toFixed(2)),
      crucibleLidMgOMassG: parseFloat(finalTotalG.toFixed(2)),
      mgMassG: parseFloat(mgMassG.toFixed(3)),
      oxygenMassG: parseFloat(Math.max(0, oxygenMassG).toFixed(3)),
      molesMg: parseFloat(molesMg.toFixed(4)),
      molesO: parseFloat(molesO.toFixed(4)),
      ratioMgToO: parseFloat(ratioRounded.toFixed(2)),
      empiricalFormula,
      recordedAt: Math.floor(s.experimentTime),
    };
    const allResults = [...s.results.filter(r => r.trialId !== s.currentTrial), result];
    const completed = allResults.length === 3;
    return {
      results: allResults,
      labStage: completed ? 'completed' : 'observed',
    };
  }),

  resetTrial: () => {
    const id = get().currentTrial;
    get().selectTrial(id);
  },

  resetAll: () => set({
    ...buildInitialTrialState(1),
    results: [],
    simulationSpeed: 1,
  }),

  setSimulationSpeed: (s) => set({ simulationSpeed: Math.max(1, Math.min(10, s)) }),

  cycleSimulationSpeed: () => set((state) => {
    const idx = SPEED_CYCLE.indexOf(state.simulationSpeed);
    const next = SPEED_CYCLE[(idx + 1) % SPEED_CYCLE.length];
    return { simulationSpeed: next };
  }),

  setCameraView: (v) => set((s) => ({ cameraView: v, cameraVersion: s.cameraVersion + 1 })),
  setShowTutorial: (show) => set({ showTutorial: show }),

  tick: (delta) => set((s) => {
    // Step animations run in real time so they remain visible at 10× speed.
    const realDelta = delta / Math.max(0.0001, s.simulationSpeed);
    const out: Partial<MgOState> = {};

    out.experimentTime = s.experimentTime + delta;

    // Smoothly settle the balance reading.
    if (Math.abs(s.currentReadingG - s.targetReadingG) > 0.001) {
      out.currentReadingG = s.currentReadingG + (s.targetReadingG - s.currentReadingG) * Math.min(1, 4 * delta);
    } else if (s.currentReadingG !== s.targetReadingG) {
      out.currentReadingG = s.targetReadingG;
    }

    // Polish animation
    if (s.isPolishingMg) {
      const p = Math.min(1, s.polishProgress + realDelta / POLISH_DURATION);
      if (p >= 1) {
        out.isPolishingMg = false;
        out.polishProgress = 1;
        out.labStage = 'mg-polished';
      } else {
        out.polishProgress = p;
      }
    }

    // Place magnesium into the crucible
    if (s.isPlacingMg) {
      const p = Math.min(1, s.placeMgProgress + realDelta / PLACE_MG_DURATION);
      const preset = TRIAL_PRESETS[s.currentTrial];
      const mgInBowl = preset.mgMassG * Math.max(0, (p - 0.55) / 0.45);
      if (p >= 1) {
        out.isPlacingMg = false;
        out.placeMgProgress = 1;
        out.mgInCrucibleG = preset.mgMassG;
        out.labStage = 'mg-placed';
      } else {
        out.placeMgProgress = p;
        out.mgInCrucibleG = mgInBowl;
      }
    }

    // Crucible move onto tripod
    if (s.isPlacingOnTripod) {
      const p = Math.min(1, s.placeOnTripodProgress + realDelta / PLACE_TRIPOD_DURATION);
      if (p >= 1) {
        out.isPlacingOnTripod = false;
        out.placeOnTripodProgress = 1;
        out.labStage = 'apparatus-ready';
      } else {
        out.placeOnTripodProgress = p;
      }
    }

    // Lid placement
    if (s.isPlacingLid) {
      const p = Math.min(1, s.lidPlaceProgress + realDelta / PLACE_LID_DURATION);
      if (p >= 1) {
        out.isPlacingLid = false;
        out.lidPlaceProgress = 1;
        out.lidIsOn = true;
        out.labStage = 'lid-on';
      } else {
        out.lidPlaceProgress = p;
      }
    }

    // Lift lid animation (with fume loss while open)
    if (s.isLiftingLid) {
      const p = Math.min(1, s.lidLiftProgress + realDelta / LIFT_LID_DURATION);
      // Open phase: 0.28..0.72 — fumes can escape
      if (p > 0.28 && p < 0.72) {
        const dt = realDelta;
        out.lidLiftSecondsTotal = s.lidLiftSecondsTotal + dt;
        out.fumeLossG = s.fumeLossG + FUME_LOSS_PER_LIFT_G * dt * 0.6;
      }
      if (p >= 1) {
        out.isLiftingLid = false;
        out.lidLiftProgress = 1;
        out.lidLiftCount = s.lidLiftCount + 1;
        // Add one-shot fume contribution per click as well.
        out.fumeLossG = (out.fumeLossG ?? s.fumeLossG) + FUME_LOSS_PER_LIFT_G * 0.6;
      } else {
        out.lidLiftProgress = p;
      }
    }

    // Crucible temperature dynamics
    let nextTemp = s.crucibleTempC;
    if (s.isHeating && !s.isCooling) {
      const target = 200 + s.heatIntensity * (STRONG_HEAT_TARGET_C - 200);
      nextTemp += (target - s.crucibleTempC) * 0.35 * delta;
      if (nextTemp > STRONG_HEAT_TARGET_C) nextTemp = STRONG_HEAT_TARGET_C;
    } else if (!s.isHeating || s.isCooling) {
      nextTemp -= Math.max(0, (s.crucibleTempC - 25)) * 0.45 * delta;
      if (nextTemp < 25) nextTemp = 25;
    }
    out.crucibleTempC = nextTemp;

    // Ignition trigger
    if (
      s.labStage === 'heating-open' &&
      s.isHeating &&
      !s.lidIsOn &&
      !s.isIgniting &&
      nextTemp > IGNITION_TEMP_C
    ) {
      out.isIgniting = true;
      out.ignitionProgress = 0;
      out.labStage = 'igniting';
    }

    // Apparatus-ready → heating-open as soon as Bunsen lights
    if (s.labStage === 'apparatus-ready' && s.isHeating) {
      out.labStage = 'heating-open';
    }

    // Ignition combustion progression
    if (s.isIgniting) {
      const p = Math.min(1, s.ignitionProgress + delta / IGNITION_DURATION);
      const preset = TRIAL_PRESETS[s.currentTrial];
      const theoreticalMgO = preset.mgMassG * (40 / 24);
      // Mg shrinks, MgO grows over the combustion duration
      out.mgInCrucibleG = preset.mgMassG * (1 - p);
      out.mgOInCrucibleG = theoreticalMgO * p;
      if (p >= 1) {
        out.isIgniting = false;
        out.ignitionProgress = 1;
        out.mgInCrucibleG = 0;
        out.mgOInCrucibleG = theoreticalMgO;
      } else {
        out.ignitionProgress = p;
      }
    }

    // Final-heating progression — keeps the crucible glowing strongly
    if (s.isFinalHeating) {
      const p = Math.min(1, s.finalHeatProgress + delta / FINAL_HEAT_DURATION);
      out.finalHeatProgress = p;
      if (p >= 1) {
        out.isFinalHeating = false;
      }
    }

    // Cooling phase: when crucible temp drops below 60 °C we're ready to weigh
    if (s.isCooling && nextTemp < 30) {
      out.isCooling = false;
    }

    // Weighing animation — crucible lifts off the bench/tripod, arcs to the
    // balance pan, sits there briefly, then arcs back home.
    if (s.isWeighing && s.weighType) {
      const p = Math.min(1, s.weighProgress + realDelta / WEIGH_DURATION);
      const preset = TRIAL_PRESETS[s.currentTrial];

      // Drop the reading to 0 the instant the crucible lifts off
      // (the pan is empty during the arc)
      if (s.weighProgress < 0.05 && p >= 0.05) {
        out.targetReadingG = 0;
      }

      // Apply the new reading as the crucible touches the pan (~progress 0.45)
      if (s.weighProgress < 0.45 && p >= 0.45) {
        if (s.weighType === 'empty') {
          out.targetReadingG = CRUCIBLE_LID_MASS_G;
        } else if (s.weighType === 'with-mg') {
          out.targetReadingG = CRUCIBLE_LID_MASS_G + preset.mgMassG;
        } else {
          const theoreticalMgO = preset.mgMassG * (40 / 24);
          const fumeCap = theoreticalMgO * FUME_LOSS_CAP_FRACTION;
          const cappedFumes = Math.min(s.fumeLossG, fumeCap);
          const finalTotalG = CRUCIBLE_LID_MASS_G + (theoreticalMgO - cappedFumes);
          out.targetReadingG = finalTotalG;
          out.lastWeighingG = finalTotalG;
        }
      }

      if (p >= 1) {
        out.isWeighing = false;
        out.weighProgress = 1;
        out.weighType = null;
        if (s.weighType === 'empty') {
          out.labStage = 'crucible-weighed';
        } else if (s.weighType === 'with-mg') {
          out.labStage = 'mg-weighed';
        } else {
          out.labStage = 'final-weighed';
        }
      } else {
        out.weighProgress = p;
      }
    }

    // Reheat cycle (compressed visual loop)
    if (s.isReheating) {
      const p = Math.min(1, s.reheatProgress + delta / REHEAT_DURATION);
      // First half heats up briefly, second half cools back down
      const reheatTemp = p < 0.5
        ? 25 + (STRONG_HEAT_TARGET_C - 25) * (p / 0.5)
        : STRONG_HEAT_TARGET_C - (STRONG_HEAT_TARGET_C - 25) * ((p - 0.5) / 0.5);
      out.crucibleTempC = reheatTemp;
      if (p >= 1) {
        out.isReheating = false;
        out.reheatProgress = 1;
        out.reheatCycle = s.reheatCycle + 1;
        out.crucibleTempC = 25;
        out.labStage = 'constant-mass';
      } else {
        out.reheatProgress = p;
      }
    }

    return out;
  }),
}));
